import React, { PropsWithChildren, useMemo } from 'react';

import PropTypes from 'prop-types';
import styledC from 'styled-components';

import theme, {
  stylePropType,
  StylableComponent,
} from '@adsk/alloy-react-theme';

import {
  TABLE_ROW_SELECTABLE_CLASSNAME,
  TABLE_ROW_SELECTED_CLASSNAME,
  TABLE_SIZES,
} from '../consts';
import getTableTheme from '../helpers/getTableTheme';
import { Themeable } from '../types';

export type TableRowProps = StylableComponent<HTMLDivElement> &
  Themeable & {
    disabled?: boolean;
    canSelect?: boolean;
    toggleSelected?: (value?: boolean, e?: React.MouseEvent) => void;
    isSelected?: boolean;
    isHighlightable?: boolean;
  };

type TableRowTrProps = {
  disabled?: boolean;
  $height: number;
  $canSelect?: boolean;
  $isHighlightable?: boolean;
  $isSelected?: boolean;
};

const StyledTableRow = styledC.tr<TableRowTrProps>`
  display: flex;
  height: ${(props) => props.$height}px;
  color: ${(props) =>
    props.disabled ? theme.tokens.colors.text.inactive.value : undefined};
  min-width: fit-content;
  ${(props) =>
    !props.$isHighlightable || props.disabled
      ? undefined
      : `
  & > [role='cell'] {
    background-color: ${
      props.$isSelected
        ? theme.tokens.colors.background.selected.subtler.value
        : theme.tokens.colors.background.primary.value
    };
  }
  &:hover {
    & > [role='cell'] {
      background-color: ${theme.tokens.colors.background.neutral.hover.value};
    }
  }
  `}
  ${(props) =>
    !props.$canSelect
      ? undefined
      : `
  cursor: pointer;
  
  &:active {
    & > [role='cell'] {
      background-color: ${theme.tokens.colors.background.neutral.pressed.value};
    }
  }
  `}
`;

const TableRow = ({
  children,
  canSelect,
  toggleSelected,
  isSelected,
  isHighlightable: isHighlightableProp,
  disabled,
  tableSize,
  style,
  className: classNameProp,
  ...props
}: PropsWithChildren<TableRowProps>) => {
  const tableSizeTheme = getTableTheme(tableSize);

  const isHighlightable =
    isHighlightableProp !== undefined ? isHighlightableProp : canSelect;

  const className = useMemo(() => {
    const classNames = [classNameProp];

    if (canSelect) {
      classNames.push(TABLE_ROW_SELECTABLE_CLASSNAME);
    }

    if (isSelected) {
      classNames.push(TABLE_ROW_SELECTED_CLASSNAME);
    }

    return classNames.join(' ');
  }, [canSelect, classNameProp, isSelected]);

  return (
    <StyledTableRow
      onClick={(e: React.MouseEvent) =>
        !disabled && canSelect && toggleSelected?.(!isSelected, e)
      }
      disabled={disabled}
      $height={tableSizeTheme.rowHeight}
      $canSelect={canSelect}
      $isHighlightable={isHighlightable}
      $isSelected={isSelected}
      className={className}
      css={style}
      {...props}
    >
      {children}
    </StyledTableRow>
  );
};

TableRow.propTypes = {
  tableSize: PropTypes.oneOf(Object.values(TABLE_SIZES)),
  canSelect: PropTypes.bool,
  toggleSelect: PropTypes.func,
  isSelected: PropTypes.bool,
  children: PropTypes.any,
  style: stylePropType,
};

export default TableRow;
